"use client";
import React, {JSX, useId} from "react";
import type {CSSProperties, ReactNode} from "react";

import {useLockedBody} from "@/shared/hooks";

import "./drawer.scss";

type Direction = "left" | "right" | "top" | "bottom";

interface DrawerProps {
    open: boolean;
    onClose: () => void;
    direction?: Direction;
    size?: string | number;
    className?: string;
    children: ReactNode;
    overlayOpacity?: number;
    overlayColor?: string;
    enableOverlay?: boolean;
    lockBackgroundScroll?: boolean;
    duration?: number;
    zIndex?: number;
    borderRadius?: string | number;
    enableBorderRadius?: boolean;
    offset?: string | number;
    offsetTop?: string | number;
    offsetBottom?: string | number;
    offsetLeft?: string | number;
    offsetRight?: string | number;
    overlayClassName?: string;
    allowOverflow?: boolean;
}

type DirectionStyle = Pick<CSSProperties, "top" | "left" | "right" | "bottom" | "width" | "height" | "transform">;

const toPx = (value: string | number | undefined): string | undefined => {
    if (value === undefined) return undefined;
    return typeof value === "number" ? `${value}px` : value;
};

/**
 * Convert vh units to dvh with fallback to custom property
 * Uses dvh (small viewport height) which is supported by modern browsers
 * Falls back to --real-viewport-height for older browsers
 * @example "85vh" → "calc(var(--real-viewport-height, 100dvh) * 0.85)"
 * @example "100vh" → "var(--real-viewport-height, 100dvh)"
 * @example "500px" → "500px"
 */
const convertVhToRealViewport = (size?: number | string): string | number | undefined => {
    if (typeof size === "string" && size.includes("vh")) {
        const match = size.match(/^(\d+(?:\.\d+)?)vh$/);
        if (match) {
            const value = parseFloat(match[1]);
            return value === 100 ? "var(--real-viewport-height, 100dvh)" : `calc(var(--real-viewport-height, 100dvh) * ${value / 100})`;
        }
    }
    return size;
};

/**
 * Get positioning styles based on drawer direction
 */
const getDirectionStyle = (dir: Direction, size?: number | string): DirectionStyle => {
    const realSize = convertVhToRealViewport(size);
    const realHeight = "var(--real-viewport-height, 100dvh)";
    const isAuto = size === "auto" || size === "fit-content";

    const styles: Record<Direction, DirectionStyle> = {
        left: {
            top: 0,
            left: 0,
            transform: "translate3d(-100%, 0, 0)",
            width: realSize,
            height: realHeight,
        },
        right: {
            top: 0,
            right: 0,
            transform: "translate3d(100%, 0, 0)",
            width: realSize,
            height: realHeight,
        },
        bottom: {
            left: 0,
            right: 0,
            bottom: 0,
            transform: "translate3d(0, 100%, 0)",
            width: "100%",
            height: realSize,
        },
        top: {
            left: 0,
            right: 0,
            top: 0,
            transform: "translate3d(0, -100%, 0)",
            width: "100%",
            height: isAuto ? "auto" : realSize,
        },
    };

    return styles[dir];
};

export function Drawer({
    open,
    onClose,
    direction = "bottom",
    size = "85vh",
    className = "",
    children,
    overlayOpacity = 0.4,
    overlayColor = "#000",
    enableOverlay = true,
    lockBackgroundScroll = true,
    duration = 300,
    zIndex = 1100,
    borderRadius = "16px",
    enableBorderRadius = true,
    offset,
    offsetTop,
    offsetBottom,
    offsetLeft,
    offsetRight,
    overlayClassName = "",
    allowOverflow = false,
}: DrawerProps): JSX.Element {
    const idSuffix = useId();

    // Lock body scroll when drawer is open
    const locked = open && lockBackgroundScroll;
    useLockedBody({locked});

    const customStyles: CSSProperties & Record<`--${string}`, string> = {
        zIndex: zIndex + 1,
        transitionDuration: `${duration}ms`,
        ...getDirectionStyle(direction, size),
    };

    if (enableBorderRadius) {
        customStyles["--drawer-border-radius"] = toPx(borderRadius)!;
    }

    if (offsetTop) customStyles["--drawer-offset-top"] = toPx(offsetTop)!;
    if (offsetBottom) customStyles["--drawer-offset-bottom"] = toPx(offsetBottom)!;
    if (offsetLeft) customStyles["--drawer-offset-left"] = toPx(offsetLeft)!;
    if (offsetRight) customStyles["--drawer-offset-right"] = toPx(offsetRight)!;

    if (offset && !offsetTop && !offsetBottom && !offsetLeft && !offsetRight) {
        customStyles["--drawer-offset"] = toPx(offset)!;
    }

    const hasOffset = offsetTop || offsetBottom || offsetLeft || offsetRight || offset;
    const classes = [
        "drawer",
        enableBorderRadius && `drawer--${direction}`,
        hasOffset && `drawer--offset-${direction}`,
        allowOverflow && "drawer--allow-overflow",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    // Overlay styles
    const overlayStyles: CSSProperties = {
        backgroundColor: overlayColor,
        opacity: overlayOpacity,
        zIndex: zIndex,
    };

    return (
        <div className="drawer-wrapper">
            <input type="checkbox" id={`drawer-checkbox-${idSuffix}`} className="drawer__checkbox" onChange={onClose} checked={open} />
            <nav role="navigation" id={`drawer-container-${idSuffix}`} style={customStyles} className={classes}>
                {children}
            </nav>
            {enableOverlay && (
                <label
                    htmlFor={`drawer-checkbox-${idSuffix}`}
                    id={`drawer-overlay-${idSuffix}`}
                    className={`drawer__overlay ${overlayClassName}`}
                    style={overlayStyles}
                />
            )}
        </div>
    );
}
